import React, { useState } from 'react';
import { Form, Input, notification } from 'components/ds';
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import { useNavigate } from 'react-router-dom';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import { useAuth } from 'auth/AuthProvider';
import routesEncapsulation from 'modules/encapsulation/Routes';
import { ValidatorField } from 'components/ValidatorField';
import { ForgotLoginLink } from './LoginPassword.styles';
import { pageConfigs } from '../../../configs/pageConfigs';
import { ApiLocalStorage } from '../../../../../utils/ApiLocalStorage';
import TurnstileCaptcha from 'components/TurnstileCaptcha';
import { useOtpContext } from '../../../context/OtpContextProvider';
import { useNavigationFlow } from '../../../hooks/useNavigationFlow';
import { FlowType } from '../../../constantes/FlowTypeEnum';
import { useGAEvents } from '../../../../../hooks/useGoogleAnalytics';

export const LoginPassword: React.FC = () => {
  const [form] = Form.useForm() as any;
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const navigate = useNavigate();
  const {login} = useAuth();
  const {data: pageConfig = {}} = useGetTemplate(pageConfigs.loginPassword.template);
  const {navigateToPasswordReset} = useNavigationFlow();
  const {setCurrentFlow} = useOtpContext();
  const { events } = useGAEvents();
  
  const username = ApiLocalStorage.get('@pwa-username');
  
  // Evento GA4: Início do formulário de senha
  React.useEffect(() => {
    events.formStart('login_password');
  }, [events]);
  
  const handleNext = async (values: any) => {
    setLoading(true);
    
    try {
      const result = await login(
        ValidatorField.removeSpecialCharsFromUsername(username),
        values.password,
        captchaToken
      );
      
      if (result) {
        // Evento GA4: Login realizado com sucesso
        events.login('password');
        events.formSubmit('login_password', true);
        
        notification.success({
          message: 'Login realizado',
          description: 'Bem-vindo à plataforma!'
        });
        setTimeout(() => {
          navigate(routesEncapsulation.home.path);
        }, 300);
      }
    } catch (error: any) {
      setCaptchaToken('');
      setCaptchaKey(prev => prev + 1);
      
      // Evento GA4: Erro no login
      events.formSubmit('login_password', false);
      events.error('login_password_error', error?.message || 'Senha inválida');
      
      notification.error({
        message: 'Senha inválida.',
        description: 'Verifique e tente novamente ou clique em "Esqueci minha senha" para recuperar o acesso.'
      })
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotLogin = () => {
    console.log('username', username);
    
    // Evento GA4: Clique no botão "Esqueci minha senha"
    events.buttonClick('forgot_password', 'login_page');
    
    if (!username) {
      events.error('forgot_password_error', 'Usuário não encontrado no armazenamento local');
      notification.error({
        message: 'Erro',
        description: 'Usuário não encontrado no armazenamento local.'
      });
      
      return;
    }
    
    // Define o fluxo como ResetPassword usando FlowType.RESET_PASSWORD
    setCurrentFlow(FlowType.RESET_PASSWORD);
    
    // Navega para a página de solicitação de OTP
    navigateToPasswordReset();
  };
  
  const submitForm = () => {
    form.validateFields()
      .then(() => {
        form.submit();
      })
      .catch((error: any) => {
        console.error('Erro de validação:', error);
      });
  };
  
  const handleVerify = (token: string) => {
    setCaptchaToken(token);
  }
  
  return (
    <PageContainer
      title={pageConfig.title}
      navigateBack={pageConfigs?.loginPassword?.navigateBack?.()}
      onClick={submitForm}
      buttonText={pageConfigs.loginPassword.buttonLabel}
      thereIsButton
      disabled={loading || !captchaToken}
      loading={loading}
      extraAboveButton={
        <ForgotLoginLink onClick={handleForgotLogin}>
          Esqueci minha senha
        </ForgotLoginLink>
      }
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={handleNext}
      >
        <Form.Item
          name='password'
          rules={[{required: true, message: 'Por favor, insira sua senha!'}]}
        >
          <Input.Password
            size='large'
            placeholder='Senha'
            autoComplete="current-password"
          />
        </Form.Item>
        <TurnstileCaptcha onVerify={handleVerify} key={captchaKey}/>
      </Form>
    </PageContainer>
  );
};
